import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { FormattedMessage } from '../../../util/reactIntl';
import { NEAR_RADIUS_MILES, SEARCH_RADIUS_MILES } from '../../../util/search';
import { haversineMiles } from '../../../util/usZip';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    searchCenter = null,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  // Two-tier results (docs/design/search-extended-radius.md): the query box
  // is SEARCH_RADIUS_MILES wide, but only kits within NEAR_RADIUS_MILES are
  // "your area" — the rest render under the "A bit farther away" divider.
  // Order within each tier preserves the user's chosen sort. Listings
  // without geolocation can't be judged far → they stay in the near tier.
  const nearListings = [];
  const farListings = [];
  listings.forEach(l => {
    const geo = l?.attributes?.geolocation;
    const miles =
      searchCenter && Number.isFinite(geo?.lat) && Number.isFinite(geo?.lng)
        ? haversineMiles(searchCenter, { lat: geo.lat, lng: geo.lng })
        : null;
    (miles != null && miles > NEAR_RADIUS_MILES ? farListings : nearListings).push(l);
  });

  const toCard = l => (
    <li key={l.id.uuid} className={css.resultItem}>
      <ListingCard
        className={css.listingCard}
        listing={l}
        renderSizes={cardRenderSizes(isMapVariant)}
        setActiveListing={setActiveListing}
        searchCenter={searchCenter}
      />
    </li>
  );

  const fartherDivider =
    farListings.length > 0 ? (
      <li key="farther-divider" className={css.fartherDivider}>
        {nearListings.length === 0 ? (
          <>
            <h2 className={css.fartherHeading}>
              <FormattedMessage
                id="SearchResultsPanel.nothingNearbyHeading"
                values={{ miles: NEAR_RADIUS_MILES }}
              />
            </h2>
            <p className={css.fartherSubtext}>
              <FormattedMessage id="SearchResultsPanel.nothingNearbySubtext" />
            </p>
          </>
        ) : (
          <>
            <h2 className={css.fartherHeading}>
              <FormattedMessage id="SearchResultsPanel.fartherAwayHeading" />
            </h2>
            <p className={css.fartherSubtext}>
              <FormattedMessage
                id="SearchResultsPanel.fartherAwaySubtext"
                values={{ from: NEAR_RADIUS_MILES, to: SEARCH_RADIUS_MILES }}
              />
            </p>
          </>
        )}
      </li>
    ) : null;

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {nearListings.map(toCard)}
        {fartherDivider}
        {farListings.map(toCard)}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
